import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useTitle } from 'react-use';

import ContextualHelp from '@/components/atoms/ContextualHelp/ContextualHelp';
import { FilterToolbar } from '@/components/organisms/FilterToolbar/FilterToolbar';
import { EmptySkeleton } from '@/components/ui/custom/EmptySkeleton';
import { useEmptyStateView } from '@/hooks/useEmptyStateView';
import useMembers from '@/hooks/useMembers';
import { useReportEmptyState } from '@/hooks/useReportEmptyState';
import { useSidebar } from '@/hooks/useSidebar';
import useLanguageModels from '@/modules/developerTools/hooks/useLanguageModels';
import { getPermissions } from '@/redux/permissions/selectors';
import useSegments from '@/segment/useSegments';
import { actions } from '@/shared/types/permissions';
import { PageName } from '@/shared/types/segment';
import { RootState } from '@/shared/types/state';
import { LANGUAGE_MODELS_DOCS_URL } from '@/util/constants';

import { EmptyLanguageModels } from './EmptyLanguageModels';
import { providerNameMapper } from './helper';
import { LanguageModelItem } from './LanguageModelItem';
import {
  CREATOR_FILTER,
  PROVIDER_FILTER,
  STATUS_FILTER,
  creatorFilterDefinition,
  providerFilterDefinition,
  statusFilterDefinition,
} from '../../components/ResourceList/filterDefinitions';
import { ResourceList } from '../../components/ResourceList/ResourceList';
import { byLastUsed, useResourceList } from '../../hooks/useResourceList';
import { LanguageModel } from '../../types';

interface LanguageModelsProps {
  onEmptyStateChange: (isEmpty: boolean) => void;
}

export const LanguageModels = ({ onEmptyStateChange }: LanguageModelsProps) => {
  const { t } = useTranslation();
  const { meta, isOpen, open } = useSidebar();
  const selectedId = isOpen('connect_language_model') ? meta?.languageModelId : undefined;
  const { languageModels, listStatus, updateLanguageModel } = useLanguageModels();
  const { getMember } = useMembers();

  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'language_models', actions.WRITE)
  );

  useTitle(t('pages.devtool_llms'));

  useSegments(PageName.LANGUAGE_MODELS);

  const handleAdd = () => open('connect_language_model');

  const handleEdit = (model: LanguageModel) =>
    open('connect_language_model', {
      languageModelId: model.language_model_id,
      provider: model.provider,
    });

  const handleToggleEnabled = (model: LanguageModel) =>
    updateLanguageModel({ language_model_id: model.language_model_id, enabled: !model.enabled });

  const { visibleItems, toolbarProps } = useResourceList(languageModels, {
    storageKey: 'languageModelsFilters',
    preferencesKey: 'language_models',
    filterValues: {
      [PROVIDER_FILTER]: (model) => model.provider,
      [STATUS_FILTER]: (model) => (model.enabled ? 'active' : 'inactive'),
      [CREATOR_FILTER]: (model) => model.created_by,
    },
    searchText: (model) => [model.model, providerNameMapper(model.provider)],
    sort: byLastUsed,
  });

  const filterDefinitions = [
    providerFilterDefinition(languageModels, providerNameMapper, t),
    statusFilterDefinition(t),
    creatorFilterDefinition(languageModels, getMember, t),
  ];

  const isLoading = listStatus === 'pending';
  const { showEmpty, showEmptySkeleton, showList, isEmptyPage } = useEmptyStateView({
    scope: 'language-models',
    isLoading,
    isEmpty: !languageModels?.length,
    isError: listStatus === 'error',
  });

  useReportEmptyState(isEmptyPage, onEmptyStateChange);

  return (
    <div className="pt-5 pb-20">
      <ContextualHelp
        title={t('developer_tools.contextual_help.language_models_title')}
        name="about_language_models"
        links={[{ label: t('common.learn_more'), url: LANGUAGE_MODELS_DOCS_URL }]}
      >
        {t('developer_tools.contextual_help.language_models_message')}
      </ContextualHelp>

      {showEmpty && <EmptyLanguageModels onAdd={handleAdd} canWrite={canWrite} />}

      {showEmptySkeleton && <EmptySkeleton descriptionLines={2} />}

      {showList && (
        <>
          <FilterToolbar
            {...toolbarProps}
            searchPlaceholder={t('developer_tools.language_models_search')}
            filterDefinitions={filterDefinitions}
          />

          <ResourceList
            isLoading={isLoading}
            isEmpty={visibleItems.length === 0}
            noResultsMessage={t('developer_tools.language_models_no_results')}
          >
            {visibleItems.map((model) => (
              <LanguageModelItem
                key={model.language_model_id}
                model={model}
                isSelected={model.language_model_id === selectedId}
                canWrite={canWrite}
                onEdit={handleEdit}
                onToggleEnabled={handleToggleEnabled}
              />
            ))}
          </ResourceList>
        </>
      )}
    </div>
  );
};
